import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from './hooks/useProducts';

interface Product {
  id: string;
  model_name: string;
  category: string;
  metal_type: string;
  purity_options: any;
  gross_weight: number;
  stone_details: any;
  making_charges: number;
  value_addition: number;
  vendor_id: string;
  description: string;
  status: string;
  images: string[];
  created_at: string;
  updated_at: string;
  delivery_days: number;
  customization_possible: boolean;
}

const ProductListing = () => {
  const navigate = useNavigate();
  const { data: products = [], isLoading, error } = useProducts();
  
  // State for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [metalFilter, setMetalFilter] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [cartItems, setCartItems] = useState<{[key: string]: number}>({});
  const [wishlistItems, setWishlistItems] = useState<string[]>([]);

  // Calculate price from making charges and value addition
  const calculatePrice = (product: Product) => {
    const makingCharges = Number(product.making_charges) || 0;
    const valueAddition = Number(product.value_addition) || 0;
    const grossWeight = Number(product.gross_weight) || 0;
    
    // Base calculation: making charges + value addition + estimated gold value
    const estimatedGoldValue = grossWeight * 5000; // Rough estimate at ₹5000 per gram
    return makingCharges + valueAddition + estimatedGoldValue;
  };

  // Get purity string from purity_options
  const getPurity = (product: Product) => {
    if (product.purity_options && Array.isArray(product.purity_options)) {
      return product.purity_options[0] || '';
    }
    return '';
  };

  // Get stone weight from stone_details
  const getStoneWeight = (product: Product) => {
    if (product.stone_details && typeof product.stone_details === 'object') {
      return product.stone_details.total_weight || 0;
    }
    return 0;
  };

  // Calculate net weight
  const getNetWeight = (product: Product) => {
    const grossWeight = Number(product.gross_weight) || 0;
    const stoneWeight = getStoneWeight(product);
    return grossWeight - stoneWeight;
  };

  // Filter & sort products
  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    
    let filtered = products.filter((product: Product) => product.status === 'active');

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter((product: Product) =>
        product.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter((product: Product) => product.category === categoryFilter);
    }

    // Metal filter
    if (metalFilter) {
      filtered = filtered.filter((product: Product) => product.metal_type === metalFilter);
    }

    // Price range filter
    if (priceRange.min) {
      filtered = filtered.filter((product: Product) => calculatePrice(product) >= parseFloat(priceRange.min));
    }
    if (priceRange.max) {
      filtered = filtered.filter((product: Product) => calculatePrice(product) <= parseFloat(priceRange.max));
    }

    // Sort products
    filtered.sort((a: Product, b: Product) => {
      switch (sortBy) {
        case 'name':
          return a.model_name.localeCompare(b.model_name);
        case 'price-low':
          return calculatePrice(a) - calculatePrice(b);
        case 'price-high':
          return calculatePrice(b) - calculatePrice(a);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [products, searchTerm, categoryFilter, metalFilter, priceRange, sortBy]);

  // Get unique categories and metals for filters
  const categories = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return [...new Set(products.map((p: Product) => p.category))];
  }, [products]);

  const metalTypes = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return [...new Set(products.map((p: Product) => p.metal_type))];
  }, [products]);

  // Add to cart functionality
  const addToCart = (productId: string, quantity: number = 1) => {
    setCartItems(prev => ({
      ...prev,
      [productId]: (prev[productId] || 0) + quantity
    }));
    
    // Show success feedback
    const product = products.find((p: Product) => p.id === productId);
    if (product) {
      // You could replace this with a toast notification
      console.log(`Added ${product.model_name} to cart`);
    }
  };

  // Add to wishlist functionality
  const toggleWishlist = (productId: string) => {
    setWishlistItems(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  // Book order functionality
  const bookOrder = (productId: string) => {
    const product = products.find((p: Product) => p.id === productId);
    if (product) {
      // Navigate to order booking with product details
      navigate('/sales/orders', { state: { selectedProduct: product } });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setMetalFilter('');
    setPriceRange({ min: '', max: '' });
    setSortBy('name');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading premium jewelry collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Catalog</h2>
          <p className="text-gray-600 mb-6">There was an error loading the jewelry catalog. Please try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  const totalCartItems = Object.values(cartItems).reduce((sum, qty) => sum + qty, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Jewelry Catalog</h1>
              <p className="text-gray-600 mt-1">
                Showing {filteredProducts.length} of {(products || []).length} premium jewelry pieces
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* Cart & Wishlist Summary */}
              <div className="flex items-center space-x-3">
                {totalCartItems > 0 && (
                  <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
                    <span className="text-blue-800 font-medium">
                      <i className="ri-shopping-cart-line mr-1"></i>
                      Cart: {totalCartItems} items
                    </span>
                  </div>
                )}
                {wishlistItems.length > 0 && (
                  <div className="bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                    <span className="text-red-800 font-medium">
                      <i className="ri-heart-line mr-1"></i>
                      Wishlist: {wishlistItems.length}
                    </span>
                  </div>
                )}
              </div>
              
              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 rounded cursor-pointer whitespace-nowrap transition-all ${
                    viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-grid-line"></i>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 rounded cursor-pointer whitespace-nowrap transition-all ${
                    viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-list-check"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Enhanced Filters Sidebar */}
          <div className="w-80 bg-white rounded-lg shadow-sm p-6 h-fit sticky top-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Refine Selection</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer whitespace-nowrap transition-colors"
              >
                Clear All
              </button>
            </div>

            {/* Search */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search Jewelry</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, description..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <i className="ri-search-line absolute left-3 top-3.5 text-gray-400"></i>
              </div>
            </div>

            {/* Category Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Jewelry Type</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            {/* Metal Type Filter */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Metal Type</label>
              <select
                value={metalFilter}
                onChange={(e) => setMetalFilter(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="">All Metals</option>
                {metalTypes.map((metal) => (
                  <option key={metal} value={metal}>{metal}</option>
                ))}
              </select>
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Price Range (₹)</label>
              <div className="flex space-x-2">
                <input
                  type="number"
                  value={priceRange.min}
                  onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                  placeholder="Min"
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <input
                  type="number"
                  value={priceRange.max}
                  onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                  placeholder="Max"
                  className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
            </div>

            {/* Sort By */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
              >
                <option value="name">Name (A-Z)</option>
                <option value="price-low">Price (Low to High)</option>
                <option value="price-high">Price (High to Low)</option>
                <option value="category">Category</option>
                <option value="newest">Newest First</option>
              </select>
            </div>

            {/* Quick Stats */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Collection Stats</h4>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Total Pieces:</span>
                  <span className="font-medium">{(products || []).length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Categories:</span>
                  <span className="font-medium">{categories.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Metal Types:</span>
                  <span className="font-medium">{metalTypes.length}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Products Grid/List */}
          <div className="flex-1">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-lg shadow-sm">
                <i className="ri-search-line text-6xl text-gray-400 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No jewelry found</h3>
                <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria to find the perfect piece</p>
                <button
                  onClick={clearFilters}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className={viewMode === 'grid' 
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                : 'space-y-4'
              }>
                {filteredProducts.map((product: Product) => {
                  const productPrice = calculatePrice(product);
                  const productPurity = getPurity(product);
                  const productNetWeight = getNetWeight(product);
                  const productStoneWeight = getStoneWeight(product);
                  const isInWishlist = wishlistItems.includes(product.id);
                  const cartQuantity = cartItems[product.id] || 0;
                  
                  return (
                    <div
                      key={product.id}
                      className={`bg-white rounded-lg shadow-sm hover:shadow-lg transition-all duration-300 ${
                        viewMode === 'list' ? 'flex items-center p-6' : 'overflow-hidden'
                      }`}
                    >
                      {viewMode === 'grid' ? (
                        // Enhanced Grid View
                        <>
                          <div className="relative group">
                            <div 
                              className="aspect-square overflow-hidden cursor-pointer"
                              onClick={() => navigate(`/products/${product.id}`)}
                            >
                              <img
                                src={product.images && product.images[0] ? product.images[0] : '/placeholder-jewelry.jpg'}
                                alt={product.model_name}
                                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/placeholder-jewelry.jpg';
                                }}
                              />
                            </div>
                            {/* Wishlist toggle */}
                            <button
                              onClick={() => toggleWishlist(product.id)}
                              className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                isInWishlist 
                                  ? 'bg-red-500 text-white shadow-lg' 
                                  : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
                              }`}
                            >
                              <i className={isInWishlist ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                            </button>
                            {/* Quick view badges */}
                            <div className="absolute bottom-3 left-3 flex flex-col space-y-1">
                              {product.customization_possible && (
                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium">
                                  Customizable
                                </span>
                              )}
                              {cartQuantity > 0 && (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                                  In Cart: {cartQuantity}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="p-4">
                            <h3 
                              className="font-semibold text-gray-900 mb-1 cursor-pointer hover:text-blue-600 transition-colors"
                              onClick={() => navigate(`/products/${product.id}`)}
                            >
                              {product.model_name}
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">{product.category}</p>
                            <p className="text-sm text-gray-500 mb-3">{product.metal_type} {productPurity}</p>
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <span className="text-lg font-bold text-gray-900">
                                  ₹{productPrice.toLocaleString('en-IN')}
                                </span>
                                <p className="text-xs text-gray-500">
                                  {Number(product.gross_weight).toFixed(1)}g total
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">
                                  Delivery: {product.delivery_days || 7} days
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => addToCart(product.id)}
                                className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-shopping-cart-line mr-1"></i>
                                Add to Cart
                              </button>
                              <button
                                onClick={() => bookOrder(product.id)}
                                className="flex-1 bg-green-600 text-white py-2 px-3 rounded-lg text-sm hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                              >
                                <i className="ri-bookmark-line mr-1"></i>
                                Book
                              </button>
                            </div>
                          </div>
                        </>
                      ) : (
                        // Enhanced List View
                        <>
                          <div className="relative">
                            <div 
                              className="w-32 h-32 flex-shrink-0 overflow-hidden rounded-lg cursor-pointer"
                              onClick={() => navigate(`/products/${product.id}`)}
                            >
                              <img
                                src={product.images && product.images[0] ? product.images[0] : '/placeholder-jewelry.jpg'}
                                alt={product.model_name}
                                className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = '/placeholder-jewelry.jpg';
                                }}
                              />
                            </div>
                            {cartQuantity > 0 && (
                              <div className="absolute -top-2 -right-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                {cartQuantity}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 ml-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 
                                    className="text-xl font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                                    onClick={() => navigate(`/products/${product.id}`)}
                                  >
                                    {product.model_name}
                                  </h3>
                                  <button
                                    onClick={() => toggleWishlist(product.id)}
                                    className={`transition-colors ${
                                      isInWishlist ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                                    }`}
                                  >
                                    <i className={isInWishlist ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                                  </button>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  {product.category} • {product.metal_type} {productPurity}
                                  {product.customization_possible && (
                                    <span className="ml-2 bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">
                                      Customizable
                                    </span>
                                  )}
                                </p>
                                <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                                  {product.description || 'Premium jewelry piece crafted with attention to detail'}
                                </p>
                                <div className="grid grid-cols-4 gap-4 text-sm text-gray-500 mb-3">
                                  <div>
                                    <span className="block font-medium text-gray-700">Gross Weight</span>
                                    <span>{Number(product.gross_weight).toFixed(1)}g</span>
                                  </div>
                                  <div>
                                    <span className="block font-medium text-gray-700">Net Weight</span>
                                    <span>{productNetWeight.toFixed(1)}g</span>
                                  </div>
                                  <div>
                                    <span className="block font-medium text-gray-700">Stone Weight</span>
                                    <span>{productStoneWeight.toFixed(1)}g</span>
                                  </div>
                                  <div>
                                    <span className="block font-medium text-gray-700">Delivery</span>
                                    <span>{product.delivery_days || 7} days</span>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right ml-6">
                                <div className="text-2xl font-bold text-gray-900 mb-2">
                                  ₹{productPrice.toLocaleString('en-IN')}
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => addToCart(product.id)}
                                    className="bg-blue-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-shopping-cart-line mr-1"></i>
                                    Add to Cart
                                  </button>
                                  <button
                                    onClick={() => bookOrder(product.id)}
                                    className="bg-green-600 text-white py-2 px-4 rounded-lg text-sm hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                                  >
                                    <i className="ri-bookmark-line mr-1"></i>
                                    Book Order
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductListing;